/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { PredictionsLegendRow } from 'src/luis/projects/analytics/components/OverallPredictionsCard/OverallChart/Legend/PredictionsLegendRow';

export type PredictionsLegendProps = {
    /**
     * Flag to indicate if the legend is in comparison mode or not.
     */
    inComparisonMode: boolean;

    /**
     * The correct percentage.
     */
    correctPercentage: number;

    /**
     * The incorrect percentage.
     */
    incorrectPercentage: number;

    /**
     * The unclear percentage.
     */
    unclearPercentage: number;

    /**
     * The difference between the correct percentages of the current version and the compared version.
     */
    correctPercentageDiff?: number;

    /**
     * The difference between the incorrect percentages of the current version and the compared version.
     */
    incorrectPercentageDiff?: number;

    /**
     * The difference between the unclear percentages of the current version and the compared version.
     */
    unclearPercentageDiff?: number;
};

const messages = defineMessages({
    correctLabel: {
        id: 'analytics.predictionsLegend.correctLabel',
        defaultMessage: 'Correct predictions'
    },
    correctHint: {
        id: 'analytics.predictionsLegend.correctHint',
        defaultMessage: 'The predicted intent matches the current intent of the example utterance.'
    },
    incorrectLabel: {
        id: 'analytics.predictionsLegend.incorrectLabel',
        defaultMessage: 'Incorrect predictions'
    },
    incorrectHint: {
        id: 'analytics.predictionsLegend.incorrectHint',
        defaultMessage: 'The predicted intent does not match the current intent of the example utterance.'
    },
    unclearLabel: {
        id: 'analytics.predictionsLegend.unclearLabel',
        defaultMessage: 'Unclear predictions'
    },
    unclearHint: {
        id: 'analytics.predictionsLegend.unclearHint',
        defaultMessage: `The top 2 predicted intents for an example utterance have very close scores.
LUIS does not use the exact same data each time it is trained so the top scoring intent may flip between the top
2 intents from training to training.`
    }
});

/**
 * The legend items that show the percentages of correct, incorrect and unclear predictions.
 *
 * @param props The percentages of correct, incorrect and unclear predictions and the differences if exist.
 */
export const PredictionsLegend = (props: PredictionsLegendProps) => {
    const {
        inComparisonMode,
        correctPercentage,
        correctPercentageDiff,
        incorrectPercentage,
        incorrectPercentageDiff,
        unclearPercentage,
        unclearPercentageDiff
    } = props;
    const { localize } = useLocalization();

    return (
        <>
            <PredictionsLegendRow
                color="correct"
                loadingDiff={inComparisonMode}
                comparisonType={false}
                percentDiff={correctPercentageDiff}
                percent={`${correctPercentage.toFixed(1)}%`}
                label={localize(messages.correctLabel)}
                description={localize(messages.correctHint)}
            />

            <PredictionsLegendRow
                color="incorrect"
                loadingDiff={inComparisonMode}
                comparisonType
                percentDiff={incorrectPercentageDiff}
                percent={`${incorrectPercentage.toFixed(1)}%`}
                label={localize(messages.incorrectLabel)}
                description={localize(messages.incorrectHint)}
            />

            <PredictionsLegendRow
                color="unclear"
                loadingDiff={inComparisonMode}
                comparisonType
                percentDiff={unclearPercentageDiff}
                percent={`${unclearPercentage.toFixed(1)}%`}
                label={localize(messages.unclearLabel)}
                description={localize(messages.unclearHint)}
            />
        </>
    );
};
